import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications
} from "sprint-common-web";

import { LanguagesDataContext } from "../../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { useOnPremAssetDetails } from "../../../common/hooks/useOnPremAssetDetails";
import { useEditOnPremSoundtrackAssetLanguage } from "./useEditOnPremSoundtrackAssetLanguage";

export interface OnPremAudioAssetLanguageFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    soundtrackId: string;
}

interface OnPremAudioAssetLanguageFormData {
    soundtrackId: string;
}

export const OnPremAudioAssetLanguageForm: React.FC<OnPremAudioAssetLanguageFormProps> = ({
    onDismiss,
    onSubmit,
    soundtrackId
}: OnPremAudioAssetLanguageFormProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { dubbingLanguages } = useContext(LanguagesDataContext);
    const { data: assetDetails } = useOnPremAssetDetails();
    const { success, error } = useNotifications();
    const {
        validateOnPremSoundtrackAssetLanguage,
        updateOnPremSoundtrackAssetLanguage
    } = useEditOnPremSoundtrackAssetLanguage({
        invalidationKey: assetDetails?.assetId
    });

    const { handleSubmit, errors, control, formState } = useForm<OnPremAudioAssetLanguageFormData>({
        defaultValues: {
            soundtrackId
        }
    });

    const handleSubtitleUpdate = async ({ soundtrackId: soundtrackLanguageId }: OnPremAudioAssetLanguageFormData) => {
        setIsLoading(true);

        try {
            const result = await validateAndSetResult(soundtrackLanguageId);

            if (!result?.isValid) {
                setIsLoading(false);
                return;
            }

            if (!assetDetails) {
                return;
            }

            const updateResponse = await updateOnPremSoundtrackAssetLanguage.mutateAsync({
                assetId: assetDetails.assetId,
                soundtrackLanguageId
            });

            onSubmit(updateResponse.newAssetId);

            success("Soundtrack language updated successfully.");
        } catch (e) {
            error("Failed to update soundtrack language.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (soundtrackLanguageId: string) => {
        if (!assetDetails) {
            return;
        }

        const result = await validateOnPremSoundtrackAssetLanguage.mutateAsync({
            assetId: assetDetails.assetId,
            soundtrackLanguageId
        });

        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleSubtitleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={dubbingLanguages.map((dub) => mapToDropdownOption(dub))}
                    label="Soundtrack"
                    name="soundtrackId"
                    control={control}
                    errors={errors}
                    disableClear={true}
                    disabled={!dubbingLanguages?.length}
                />
                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
